import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from '@angular/router';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { UserService } from '../service/user.service';
import { NavigationConfigService } from '../service/navigation-config.service';
import { environment } from '../environments/environment';

// Matches the server's JWT ClockSkew (Program.cs) so the client is never
// stricter than the API about token expiry.
const CLOCK_SKEW_SECONDS = 300;

// Reads the "role", "userId" and "exp" claims out of the JWT payload. The
// signature cannot be verified in the browser (HS256) — this only detects a
// stored role/userId that has been edited away from what the server put in
// the token; the API still verifies the signature on every call.
function readTokenClaims(token: string): { role: string; userId: string; exp?: number } | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const bytes = Uint8Array.from(atob(padded), c => c.charCodeAt(0));
    const payload = JSON.parse(new TextDecoder().decode(bytes));
    if (!payload || typeof payload !== 'object' || typeof payload.role !== 'string' || !payload.role) {
      return null;
    }
    return {
      role: payload.role,
      userId: payload.userId === undefined || payload.userId === null ? '' : String(payload.userId).trim(),
      exp: typeof payload.exp === 'number' ? payload.exp : undefined
    };
  } catch {
    return null;
  }
}

// Returns the verified role (the JWT claim) when the session is intact, or
// null when there is no token, it is malformed/expired, or the stored role or
// userId does not match the token's claims (an edited userId would otherwise
// let the page shell load and fire its dropdown/lookup calls under a
// foreign identity).
function getVerifiedRole(userService: UserService): string | null {
  const token = userService.getAccessToken();
  if (!token) {
    return null;
  }
  const claims = readTokenClaims(token);
  if (!claims) {
    return null;
  }
  if (claims.exp !== undefined && Date.now() > (claims.exp + CLOCK_SKEW_SECONDS) * 1000) {
    return null;
  }
  if (userService.getRole() !== claims.role) {
    return null;
  }
  if (!claims.userId || String(userService.getUserId()) !== claims.userId) {
    return null;
  }
  return claims.role;
}

// Single rejection path for a missing/invalid/tampered session — same
// behaviour as SessionTimeoutService.logout().
function rejectSession(router: Router): boolean | UrlTree {
  localStorage.clear();
  sessionStorage.clear();
  if (environment.isLogoOutFromLive === '1') {
    window.location.href = 'https://login.teamleaseregtech.com/logout.aspx';
    return false;
  }
  return router.createUrlTree(['/Login']);
}

// Blocks unauthorized route activation before the target component (and its
// data-loading API calls) is ever created — fixes VAPT Broken Access Control
// (Role Bypass): pasting/deep-linking a URL for another role's page used to
// let that page's ngOnInit fire before LayoutComponent's post-render check
// could redirect. The role is taken from the JWT claim and the stored role
// must match it, so editing sessionStorage no longer opens other roles'
// pages. See ADR-032 / ADR-036.
//
// The allowed-route list is no longer the static src/data/ValidRouts.ts. That file
// shipped EVERY role's permission matrix in the served JS bundle, so any user could
// read the full cross-role map in DevTools and knew exactly which screens to try —
// and could edit the in-memory array to restore access to all of them. The list now
// comes from /api/MenuHandler/GetNavigationConfig, which derives role and customer
// from the caller's JWT claims, so the client only ever holds its own role's slice.
// NavigationConfigService caches it in memory, so this resolves synchronously after
// the first navigation. If the config cannot be fetched, access is denied
// (deny-by-default).
//
// NOTE: this is a CLIENT-side control — it protects navigation, not data. The
// authoritative control for an authenticated lower-privilege token is the server-side
// role → endpoint matrix in dbo.RoleApiAccessMapping, enforced by
// RoleAuthorizationMiddleware in the API. Both layers are required.
export const roleGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const router = inject(Router);
  const userService = inject(UserService);
  const navConfigService = inject(NavigationConfigService);

  // A verified role also guarantees the access token is present, which
  // GetNavigationConfig ([Authorize]) needs — so the config call is never
  // reached with an empty Bearer.
  const role = getVerifiedRole(userService);
  if (!role) {
    return rejectSession(router);
  }

  const routePath = route.routeConfig?.path;
  if (!routePath) {
    return router.createUrlTree(['/Error', '403']);
  }

  // The server already applied the role rules; the client only checks membership
  // in its own slice. Any failure (401/500/network) denies.
  return navConfigService.getConfig().pipe(
    map(config => config.routes.includes(routePath)
      ? true
      : router.createUrlTree(['/Error', '403'])),
    // 401 = the API no longer accepts this token (expired, or invalidated by a newer login /
    // logout elsewhere) - that is a dead session, so send the user to Login instead of a
    // misleading 403 page. Any other failure (500/network) still denies.
    catchError((err) => of(err?.status === 401 ? rejectSession(router) : router.createUrlTree(['/Error', '403'])))
  );
};

// Any valid logged-in session, regardless of role (e.g. ChangePassword,
// which every role may open but which must not render unauthenticated).
export const authGuard: CanActivateFn = () => {
  const router = inject(Router);
  const userService = inject(UserService);

  if (!getVerifiedRole(userService)) {
    return rejectSession(router);
  }
  return true;
};
